/**
 * ExtractionRewardScreen — the card draft shown after a successful
 * extraction op. Mirrors the HQ pack UI visually so the interaction
 * feels familiar, but differs in three ways:
 *   1. No mark cost — the draft IS the reward for the extraction.
 *   2. Category is pre-rolled (random among intel / tactics / fieldcraft)
 *      rather than player-chosen.
 *   3. Severance passive widens the draft to 5 cards instead of 3.
 *
 * Pick one → added to the run deck. Skip → no card, no penalty.
 * Either way the player continues to the run-path map.
 */

import React, { useMemo, useState, useCallback } from 'react';
import { theme } from '../../styles/theme';
import { Card, CardCategory } from '../../types/game';
import { RunState } from '../../types/run';
import { getExtractionRewardOptions, PackType } from '../../engine/runState';

const CATEGORY_COLORS: Record<CardCategory, string> = {
  movement: '#66ccff',
  intel: '#aa88ff',
  tactics: '#ffaa44',
  combat: '#ff5555',
  fieldcraft: '#44cc88',
  junk: '#777777',
};

const PACK_INFO: Record<PackType, { label: string; color: string }> = {
  intel:      { label: 'INTEL',      color: '#aa88ff' },
  tactics:    { label: 'TACTICS',    color: '#ffaa44' },
  fieldcraft: { label: 'FIELDCRAFT', color: '#44cc88' },
};

interface ExtractionRewardScreenProps {
  runState: RunState;
  /** Called when the player picks a card to add. */
  onPick: (card: Card) => void;
  /** Called when the player declines the reward. */
  onSkip: () => void;
}

export function ExtractionRewardScreen({
  runState,
  onPick,
  onSkip,
}: ExtractionRewardScreenProps) {
  // Roll options once on mount — re-rolling on every render would cheat.
  const { category, cards } = useMemo(
    () => getExtractionRewardOptions(runState),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );
  const [picked, setPicked] = useState(false);
  const packInfo = PACK_INFO[category];

  const handlePick = useCallback((card: Card) => {
    if (picked) return; // double-click guard
    setPicked(true);
    onPick(card);
  }, [picked, onPick]);

  return (
    <div style={styles.container}>
      {/* Top strip: extraction-success badge */}
      <div style={styles.successBadge}>
        <div style={styles.successLabel}>EXTRACTION SUCCESSFUL</div>
        <div style={styles.successSubtitle}>Asset secured — intercept intel recovered</div>
      </div>

      {/* Pack title */}
      <div style={{ ...styles.packTitle, color: packInfo.color }}>
        {packInfo.label} INTEL RECOVERED
      </div>
      <div style={styles.packSubtitle}>
        Pick one card to add to your deck:
      </div>

      {/* Revealed cards */}
      <div style={styles.cardRow}>
        {cards.map(card => {
          const catColor = CATEGORY_COLORS[card.category];
          return (
            <div
              key={card.id}
              className="extraction-reward-card"
              style={{
                ...styles.card,
                borderColor: `${catColor}40`,
                borderTopColor: catColor,
              }}
              onClick={() => handlePick(card)}
              onMouseEnter={e => {
                (e.currentTarget as HTMLElement).style.borderColor = catColor;
                (e.currentTarget as HTMLElement).style.boxShadow = `0 0 24px ${catColor}40`;
                (e.currentTarget as HTMLElement).style.transform = 'translateY(-4px)';
              }}
              onMouseLeave={e => {
                (e.currentTarget as HTMLElement).style.borderColor = `${catColor}40`;
                (e.currentTarget as HTMLElement).style.boxShadow = 'none';
                (e.currentTarget as HTMLElement).style.transform = 'none';
              }}
            >
              <div style={styles.cardApCost}>{card.apCost}</div>
              <div style={styles.cardName}>{card.name}</div>
              <div style={{ ...styles.cardCategory, color: catColor }}>
                {card.category.toUpperCase()}
              </div>
              {card.movementRange && (
                <div style={{ ...styles.cardRange, color: catColor }}>
                  {card.movementRange}m range
                </div>
              )}
              <div style={styles.cardDesc}>{card.description}</div>
              {card.condition && (
                <div style={styles.cardCondition}>{card.condition}</div>
              )}
              {(card.isBurn || card.isChain) && (
                <div style={styles.badgeRow}>
                  {card.isBurn && <div style={{ ...styles.badge, color: '#ff5032', background: 'rgba(255, 80, 50, 0.15)' }}>BURN</div>}
                  {card.isChain && <div style={{ ...styles.badge, color: '#44aaff', background: 'rgba(68, 170, 255, 0.15)' }}>CHAIN</div>}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <button
        className="run-map-button"
        style={styles.skipButton}
        onClick={onSkip}
      >
        NO THANKS
      </button>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(8, 12, 20, 0.98)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    fontFamily: theme.fonts.mono,
    padding: '32px',
    overflow: 'auto',
  },
  successBadge: {
    textAlign: 'center',
    marginBottom: '40px',
  },
  successLabel: {
    fontSize: '14px',
    letterSpacing: '8px',
    fontWeight: 700,
    color: '#44cc88',
    textShadow: '0 0 20px rgba(68, 204, 136, 0.5)',
  },
  successSubtitle: {
    fontSize: '10px',
    letterSpacing: '2px',
    color: theme.colors.textSecondary,
    marginTop: '8px',
  },
  packTitle: {
    fontSize: '14px',
    fontWeight: 700,
    letterSpacing: '4px',
    marginBottom: '12px',
  },
  packSubtitle: {
    fontSize: '11px',
    color: theme.colors.textSecondary,
    letterSpacing: '1px',
    marginBottom: '32px',
  },
  cardRow: {
    display: 'flex',
    gap: '24px',
    justifyContent: 'center',
    marginBottom: '32px',
    flexWrap: 'wrap',
    maxWidth: '1200px',
  },
  card: {
    width: '220px',
    height: '300px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '6px',
    position: 'relative',
    background: 'rgba(12, 16, 24, 0.92)',
    border: '1px solid',
    borderTop: '4px solid',
    borderRadius: '8px',
    cursor: 'pointer',
    fontFamily: theme.fonts.mono,
    transition: 'all 0.15s',
    padding: '24px 16px 16px',
  },
  cardApCost: {
    position: 'absolute',
    top: '10px',
    left: '12px',
    fontSize: '26px',
    fontWeight: 700,
    color: theme.colors.accentYellow,
  },
  cardName: {
    fontSize: '18px',
    fontWeight: 700,
    color: theme.colors.textPrimary,
    letterSpacing: '1px',
    textAlign: 'center',
    marginTop: '10px',
  },
  cardCategory: {
    fontSize: '10px',
    letterSpacing: '2.5px',
    fontWeight: 700,
  },
  cardRange: {
    fontSize: '12px',
    letterSpacing: '1px',
    fontWeight: 600,
    marginTop: '2px',
  },
  cardDesc: {
    fontSize: '11px',
    color: theme.colors.textSecondary,
    lineHeight: '1.55',
    textAlign: 'center',
    padding: '8px 4px 0',
    flex: 1,
  },
  cardCondition: {
    fontSize: '9px',
    color: '#ff8844',
    lineHeight: 1.4,
    textAlign: 'center',
    padding: '0 4px',
    letterSpacing: '0.5px',
    marginTop: '4px',
  },
  badgeRow: {
    display: 'flex',
    gap: '4px',
    marginTop: '6px',
  },
  badge: {
    fontSize: '8px',
    fontWeight: 700,
    letterSpacing: '1px',
    padding: '2px 6px',
    borderRadius: '2px',
  },
  skipButton: {
    background: 'none',
    border: `1px solid ${theme.colors.border}`,
    color: theme.colors.textSecondary,
    padding: '10px 24px',
    fontSize: '10px',
    letterSpacing: '2px',
    cursor: 'pointer',
    fontFamily: theme.fonts.mono,
    borderRadius: '4px',
  },
};
